import React, { useContext, useEffect } from "react";
import qs from "qs";
import { observer } from "mobx-react";
import { useLocation, useParams } from "react-router-dom";
import classNames from "classnames";
import { Button, Dropdown, Menu } from "antd";

import { Page } from "@app/components/page";
import { download } from "@app/lib/file";
import BookmarkSidebar from "@app/components/document/bookmarks";
import PdfViewer from "@app/components/pdf-viewer";
import ExcelViewer from "@app/components/excel-viewer";

import state from "@app/state/store/report/document/details";
import bookmarksStore from "@app/state/store/report/document/bookmarks";
import applicationStore from "@app/state/store/application";
import EmptyList from "@app/components/list/empty";
import report from "@app/state/store/report";
import ChatIcon from "@app/components/global-chat/icon";
import chatStore from "@app/state/store/ai-chat";
import { FullPageContainerContext } from "@app/components/layout/full-page/full-page";
import { AI_SOURCE_TYPE } from "@app/constants";

import "./style/preview.scoped.scss";

import BookmarkTab from "./bookmark-tab";
import ImagePreview from "./image-preview";
import { CloudDownloadOutlined, EllipsisOutlined, FileExcelOutlined } from "@ant-design/icons";

function parseParams({ area, excel }) {
    let json;
    try {
        if (area) {
            json = JSON.parse(area);
        } else if (excel) {
            json = JSON.parse(excel);
        }

        // eslint-disable-next-line
    } catch (ex) {}

    return json;
}

const Bookmarks = observer(({ className, ...rest }) => {
    const document = state.document;
    const file = document?.file;
    const preview = document?.preview || file;
    const location = useLocation();
    const readonly = report.readOnly;

    const { document: documentId } = useParams();
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    const isFullPage = "fp" === location.pathname.split("/")[1];
    const chatContainer = useContext(FullPageContainerContext);

    const load = documentId === document?._id;
    let goto = load ? parseParams(params) : undefined;
    const pageTitle = document?.name ?? "Bookmarks";

    useEffect(() => {
        if (documentId && document?._id !== documentId) {
            state.load(documentId);
        } else if (!documentId && document?._id) {
            state.reset();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId]);

    useEffect(() => {
        if (document?._id) {
            chatStore.setActiveDocument({
                id: document._id,
                type: AI_SOURCE_TYPE.SOURCE_DOCUMENT,
                hasFile: !!document.file,
                name: document.name,
            });
        }

        return () => {
            chatStore.setActiveDocument(null);
        };
    }, [document?._id, document?.file, document?.name]);

    if (document?.deleted) {
        return (
            <div className="removedDocument">The source document was removed from the system</div>
        );
    }

    const enableBookmarkExport = !!bookmarksStore.filteredGroupedBookmarks?.length;
    const menu = (
        <Menu className="menu">
            {file?._id && (
                <Menu.Item
                    onClick={() => {
                        download(file._id);
                    }}
                    icon={<CloudDownloadOutlined />}
                >
                    Download
                </Menu.Item>
            )}

            <Menu.Item icon={<FileExcelOutlined />} disabled={!enableBookmarkExport}>
                {enableBookmarkExport ? (
                    <a
                        href={bookmarksStore.exportBookmarkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Export Bookmarks
                    </a>
                ) : (
                    "Export Bookmarks"
                )}
            </Menu.Item>
        </Menu>
    );

    return (
        <Page className={classNames("bookmarks", className)} {...rest}>
            <Page.Header>
                <Page.Header.Left>
                    <Page.Title breadcrumbs={!isFullPage}>{pageTitle}</Page.Title>
                </Page.Header.Left>
                <Page.Header.Right shrink>
                    {isFullPage && chatStore.enabled && (
                        <div className="chat-trigger">
                            <ChatIcon container={chatContainer} />
                        </div>
                    )}
                    <Dropdown overlay={menu} trigger="click" placement="bottomRight">
                        <Button icon={<EllipsisOutlined />} />
                    </Dropdown>
                </Page.Header.Right>
            </Page.Header>

            <Page.Layout>
                <Page.Body id="document-viewer" className="page-body" padding={false}>
                    <Viewer preview={preview} goto={goto} document={document} file={file} />
                </Page.Body>

                <BookmarkSidebar
                    document={document}
                    readonly={readonly}
                    isFullPage={isFullPage}
                    enableAI={applicationStore.enableAI}
                />
            </Page.Layout>
        </Page>
    );
});

const Viewer = observer(({ preview, document, goto, file }) => {
    const { document: documentId } = useParams();

    const { search } = useLocation();

    const params = qs.parse(search, { ignoreQueryPrefix: true });
    const focusArea = goto && !!params.focus;

    const highlights = [];
    const selectedBookmark = bookmarksStore?.bookmarks?.find(
        (bookmark) => bookmark._id === params.bid,
    );

    if (selectedBookmark) {
        highlights.push(selectedBookmark);
    }

    /**
     * Handle the add highlight event
     */
    const addBookmark = (bookmark) => {
        bookmark.document = document._id;
        bookmarksStore.save(bookmark);
    };

    const renderPage = (props) => {
        const bookmarks = bookmarksStore?.bookmarks || [];

        return (
            <>
                {props.canvasLayer.children}
                {bookmarks
                    .filter(
                        (bookmark) =>
                            bookmark?.areas[0]?.pageIndex === props.pageIndex &&
                            bookmark?.document === documentId,
                    )
                    .map((bookmark) => (
                        <BookmarkTab key={bookmark._id} bookmark={bookmark} />
                    ))}
                {props.textLayer.children}
                {props.annotationLayer.children}
            </>
        );
    };

    if (!preview?._id) {
        return (
            <EmptyList title="No Document Selected" show>
                Please pick a bookmark to preview a document.
            </EmptyList>
        );
    } else if (preview.isImage) {
        return <ImagePreview file={file} document={document} onBookmark={addBookmark} />;
    } else if (preview.isExcel) {
        return <ExcelViewer fileId={preview._id} goto={goto} onBookmark={addBookmark} />;
    } else {
        return (
            <PdfViewer
                fileId={preview._id}
                goto={goto}
                focus={focusArea}
                onBookmark={addBookmark}
                renderPage={renderPage}
                highlights={highlights}
            />
        );
    }
});

export default Bookmarks;
